import type { Pool } from '../types/domain';

export type PoolStatusCardModel = {
  id: string;
  code: string;
  name: string;
  location: string;
  operatingStatus: string;
  waterQuality: {
    status: 'good' | 'warning' | 'critical';
    ph: number;
    chlorine: number;
    temperature: number;
  };
  safety: {
    status: 'safe' | 'warning' | 'danger';
    incidents: number;
  };
  usage: {
    current: number;
    capacity: number;
    percentage: number;
  };
  devices: {
    total: number;
    online: number;
    percentage: number;
  };
  alerts: {
    total: number;
    critical: number;
  };
  lastUpdated: string;
};

function waterTone(
  wq: Pool['water_quality'] | undefined
): 'good' | 'warning' | 'critical' {
  if (!wq) return 'good';
  const ph = Number(wq.ph);
  const cl = Number(wq.chlorine ?? wq.chlorine_level);
  const temp = Number(wq.temperature);
  if (
    (Number.isFinite(ph) && (ph < 6.8 || ph > 7.8)) ||
    (Number.isFinite(cl) && (cl < 0.5 || cl > 2.5)) ||
    (Number.isFinite(temp) && (temp < 24 || temp > 32))
  ) {
    return 'warning';
  }
  if (
    (Number.isFinite(ph) && (ph < 6.5 || ph > 8.2)) ||
    (Number.isFinite(cl) && cl > 3.5)
  ) {
    return 'critical';
  }
  return 'good';
}

function safetyTone(
  pool: Pool,
  incidentCount: number
): 'safe' | 'warning' | 'danger' {
  const st = String(pool.status || '').toLowerCase();
  const ss = pool.safety_status?.status?.toLowerCase?.() ?? '';
  if (st === 'alert' || st === 'error' || ss === 'danger' || ss === 'critical') {
    return 'danger';
  }
  if (
    st === 'maintenance' ||
    ss === 'warning' ||
    incidentCount > 0
  ) {
    return 'warning';
  }
  return 'safe';
}

export function buildPoolStatusCard(
  pool: Pool,
  opts?: {
    cctvOnline?: number;
    cctvTotal?: number;
    alertTotal?: number;
    alertCritical?: number;
    incidentCount?: number;
  }
): PoolStatusCardModel {
  const capacity = Math.max(1, Number(pool.capacity) || 1);
  const current = Math.max(0, Number(pool.current_occupancy) || 0);
  const pct = Math.min(100, Math.round((current / capacity) * 100));
  const wq = pool.water_quality;
  const cctvTotal = Math.max(0, opts?.cctvTotal ?? pool.cctvs?.length ?? 0);
  const cctvOnline = Math.max(
    0,
    Math.min(
      cctvTotal,
      opts?.cctvOnline ??
        pool.cctvs?.filter((c) => {
          const s = String(c.status || '').toUpperCase();
          return s === 'ACTIVE' || s === 'ONLINE' || c.is_active;
        }).length ??
        0
    )
  );
  const devPct = cctvTotal > 0 ? Math.round((cctvOnline / cctvTotal) * 100) : 0;
  const incidents = opts?.incidentCount ?? 0;
  const updated = pool.updated_at
    ? new Date(pool.updated_at).toLocaleString('ko-KR')
    : '—';

  return {
    id: String(pool.code || pool.id || ''),
    code: String(pool.code || pool.id || ''),
    name: pool.name || pool.code || '시설',
    location: pool.location || pool.address || '—',
    operatingStatus: String(pool.status || 'active'),
    waterQuality: {
      status: waterTone(wq),
      ph: Number(wq?.ph) || 0,
      chlorine: Number(wq?.chlorine ?? wq?.chlorine_level) || 0,
      temperature: Number(wq?.temperature) || 0,
    },
    safety: {
      status: safetyTone(pool, incidents),
      incidents,
    },
    usage: {
      current,
      capacity,
      percentage: pct,
    },
    devices: {
      total: cctvTotal,
      online: cctvOnline,
      percentage: devPct,
    },
    alerts: {
      total: opts?.alertTotal ?? 0,
      critical: opts?.alertCritical ?? 0,
    },
    lastUpdated: updated,
  };
}
